import { useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useListUsagesQuery } from '../api/authoring'
import type { Document, Manifest } from '../api/authoring'
import { problemMessage } from '../api/baseApi'
import { useIntent, shown } from '../api/useIntent'
import { LISTING_LAG } from '../model/labels'
import { uploadsInto } from '../model/manifest'
import { Loading, QueryError } from './QueryState'

/**
 * Deleting a component, and saying what that actually means.
 *
 * **Delete is "remove from the library".** It appends
 * `component.tombstoned`, which takes the component out of every listing
 * and every picker; the stream is never truncated, so it still reads by
 * id and the platform could revert it — though this client does not
 * offer that yet (`UndoButton`, B25). That is not a softened version of a
 * real delete — it is what deleting has to be in a model where a pin is a
 * promise that a published tree does not change under you.
 *
 * Which is why the dialog leads with the pin state rather than a count.
 * A placement pinned to a revision keeps resolving to it forever; an
 * unpinned one resolves to the target's latest at every composition, and
 * after a tombstone that is nothing, so the placement composes away. The
 * author is being asked about the second group and only the second group.
 *
 * **And it says what the lookup cannot see**, which is the part it would
 * be easiest and worst to leave out. A usage is a compositional edge. A
 * URL in a background or in a content record's markdown is not a child,
 * so it has no pin state, no invalidation and no row — a component a page
 * is visibly showing can report here as unused. An author reading silence
 * as safety is the failure this paragraph exists to prevent.
 */
export function DeleteComponentDialog({
  tenant,
  open,
  document,
  manifest,
  onClose,
  onDeleted,
}: {
  tenant: string
  open: boolean
  document: Document
  manifest: Manifest | undefined
  onClose: () => void
  onDeleted: () => void
}) {
  const held = useListUsagesQuery({ tenant, id: document.id }, { skip: !open })
  const { send, isLoading: sending, error } = useIntent(tenant)
  const [confirming, setConfirming] = useState(false)

  const usages = held.data?.usages ?? []
  const placements = usages.reduce((total, usage) => total + usage.placements, 0)
  const pinned = usages.reduce((total, usage) => total + usage.pinned, 0)
  const breaking = placements - pinned

  const remove = async () => {
    setConfirming(true)
    try {
      // One intent, no payload. `component.tombstoned` reflects an empty
      // struct: deleting states nothing beyond which component, and the
      // actor and the moment are the surface's.
      await send(document.id, 'component.tombstoned', {})
      onDeleted()
    } finally {
      setConfirming(false)
    }
  }

  const busy = sending || confirming
  // Asked of the document rather than of its type: only some of a
  // type's components may hold a file, and promising that an uploaded
  // one is kept says nothing true about one that points elsewhere.
  const holdsAFile = uploadsInto(manifest, document.type, document.fields)

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Delete <code>{document.id}</code>?
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            This removes it from the library, so it can no longer be chosen. It cannot be undone.
            {holdsAFile && ' The uploaded file is kept, so anything already published keeps working.'}
          </Typography>

          {held.isLoading && <Loading label="Checking where it is used…" />}
          {held.error && <QueryError error={held.error} title="Could not check where it is used" />}

          {!held.isLoading && !held.error && (
            <Alert severity={breaking > 0 ? 'warning' : 'info'}>
              <AlertTitle>
                {usages.length === 0
                  ? 'It is not used anywhere'
                  : `Used in ${placements} place${placements === 1 ? '' : 's'} across ${usages.length} item${
                      usages.length === 1 ? '' : 's'
                    }`}
              </AlertTitle>
              {breaking > 0 ? (
                <>
                  {breaking} of them will show nothing once this is deleted and the site is next published.
                  {pinned > 0 &&
                    ` The other ${pinned} ${pinned === 1 ? 'is' : 'are'} held at an earlier version and will keep working.`}
                </>
              ) : placements > 0 ? (
                <>
                  Every one of them is held at an earlier version, so none of them changes.
                </>
              ) : (
                <>Nothing appears to use it. Read the note below before relying on that.</>
              )}
            </Alert>
          )}

          {usages.length > 0 && (
            <List dense disablePadding sx={{ maxHeight: 220, overflowY: 'auto' }}>
              {usages.map((usage) => (
                <ListItem key={usage.id} disableGutters secondaryAction={<Chip size="small" label={usage.type} />}>
                  <ListItemText
                    primary={usage.label ?? usage.id}
                    secondary={
                      usage.placements === 1
                        ? usage.pinned === 1
                          ? 'used once, held at an earlier version'
                          : 'used once'
                        : `used ${usage.placements} times, ${usage.pinned} held at an earlier version`
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {/* The honest limit, stated at the moment it matters rather
              than in documentation nobody reads before pressing a red
              button. */}
          <Alert severity="info">
            <AlertTitle>This check is not complete</AlertTitle>
            It only finds places where this was added as a block or picture. A file referred to by its web address, such
            as a background image, is not found, so something a page is visibly showing may still be listed as unused.
            <br />
            <br />
            {LISTING_LAG}
          </Alert>

          {error && <Alert severity="error">{problemMessage(error, 'It could not be deleted.')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={() => remove().catch(shown)} disabled={busy}>
          Remove from the library
        </Button>
      </DialogActions>
    </Dialog>
  )
}
